import { ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localePl from '@angular/common/locales/pl';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CORE_INTERCEPTORS, provideCore } from '@corelabs/angular/core';
import { provideAccount } from '@corelabs/angular/account';
import { provideMessages } from '@corelabs/angular/messages';
import { provideClI18n } from '@corelabs/angular/ui';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';

import { environment } from '@env/environment';

import { apiErrorMessageInterceptor, HttpService } from './core/http';
import { appRoutes } from './app.routing';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { clI18nConfig, customMissingTranslationHandler, translateLoaderStatic } from './i18n';
import { provideQrCodesStore } from './store/qr-codes';
import { provideSessionStore } from './store/session';

registerLocaleData(localePl);

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        // number/date pipes format the Polish way (`1 482`); registerLocaleData above must follow if this changes
        { provide: LOCALE_ID, useValue: 'pl-PL' },
        provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ onSameUrlNavigation: 'reload' })),
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.sw,
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideHttpClient(withFetch(), withInterceptors([...CORE_INTERCEPTORS, apiErrorMessageInterceptor])),
        provideStore(),
        // A `define` literal, not environment.production — only a literal folds away, dropping the instrumentation.
        ...(NGRX_DEVTOOLS ? [provideStoreDevtools({ maxAge: 25, connectInZone: true })] : []),
        provideEffects(),
        provideCore({ serverUrl: environment.serverUrl + '/api/v1/admin/' }),
        // After provideCore() so the app's HttpService (corelabs client + app fluent methods) wins the HttpClient token.
        {
            provide: HttpClient,
            useClass: HttpService,
        },
        // Skip the library's vague error toast — apiErrorMessageInterceptor already shows a specific one.
        provideAccount({
            skip: ['logout-error', 'activate-and-set-password-error', 'reset-password-error', 'reset-password-request-error'],
        }),
        provideMessages({ secondsShown: 5, visibleCount: 2, updateInterval: 100 }),
        provideClI18n(clI18nConfig),
        ...provideSessionStore(),
        ...provideQrCodesStore(),
        // loading starts with the service, so labels asked for before LanguageService.init() wait for the file instead of missing
        provideTranslateService({
            lang: environment.languages.default,
            fallbackLang: environment.languages.default,
            loader: { provide: TranslateLoader, useFactory: translateLoaderStatic },
            missingTranslationHandler: { provide: MissingTranslationHandler, useFactory: customMissingTranslationHandler },
        }),
    ],
};
